/**
 * Location Type Helpers
 * Utilities to determine location types and categorize them
 */

import { ChatLocation } from '@/types/chatTypes';

/**
 * Google Places API types that indicate a business/establishment
 * rather than a tourist attraction or place
 */
const BUSINESS_TYPES = [
  'store',
  'shopping_mall',
  'supermarket',
  'restaurant',
  'cafe',
  'bar',
  'night_club',
  'bank',
  'atm',
  'gas_station',
  'pharmacy',
  'hospital',
  'doctor',
  'dentist',
  'veterinary_care',
  'health',
  'gym',
  'spa',
  'hair_care',
  'beauty_salon',
  'car_repair',
  'car_wash',
  'car_dealer',
  'real_estate_agency',
  'lawyer',
  'accounting',
  'insurance_agency',
  'travel_agency',
  'lodging',
  'office',
  'establishment',
  'finance',
  'food',
  'meal_takeaway',
  'meal_delivery',
  'clothing_store',
  'shoe_store',
  'jewelry_store',
  'book_store',
  'electronics_store',
  'furniture_store',
  'home_goods_store',
  'hardware_store',
  'convenience_store',
  'department_store',
  'florist',
  'liquor_store',
  'pet_store',
  'bicycle_store',
  'moving_company',
  'plumber',
  'electrician',
  'locksmith',
  'painter',
  'roofing_contractor',
  'general_contractor',
  'subway_station',
  'bus_station',
  'train_station',
  'airport',
  'parking',
  'post_office',
  'library',
  'school',
  'university',
  'church',
  'mosque',
  'synagogue',
  'hindu_temple',
  'temple',
  'city_hall',
  'courthouse',
  'police',
  'fire_station',
  'embassy',
  'local_government_office',
  // Additional common business types
  'bakery',
  'meal_delivery',
  'meal_takeaway',
  'shopping_center',
  'market',
  'wholesale',
  'retail',
  'business',
  'company',
  'corporation',
  'firm',
  'shop',
  'mall',
  'outlet',
];

/**
 * Check if a location is a business/establishment (shop, building, office, etc.)
 * rather than a tourist place
 *
 * We want to show popup for: restaurants, cafes, shops, stores, malls, offices with business operations
 * We want to show place details for: tourist attractions, landmarks, monuments, parks, museums, etc.
 *
 * IMPORTANT: The API response no longer includes business_status, opening_hours_text, or rating.
 * All classification is based solely on the types array.
 *
 * DEFAULT BEHAVIOR: If we're not 100% certain it's a business, we default to treating it as a place.
 * This ensures places of interest and tourist attractions are shown in the places tab.
 */
export const isBusinessLocation = (location: ChatLocation): boolean => {
  // If no types available, default to place (not a business)
  // Since the new API response doesn't include business_status, opening_hours, etc.,
  // we rely entirely on the types array
  if (!location.types || location.types.length === 0) {
    return false; // Default to place when no type information is available
  }

  // Normalize types to lowercase for comparison
  const normalizedTypes = location.types.map(t => t.toLowerCase().trim());

  // PRIORITY 0: Check for STRONG place types FIRST (before anything else)
  // Tourist attractions should ALWAYS be treated as places, even if they have business_status
  const strongPlaceTypes = [
    'tourist_attraction',
    // Removed 'point_of_interest' - it's too generic, many businesses have this
    'park',
    'museum',
    'art_gallery',
    'zoo',
    'aquarium',
    'amusement_park',
    'stadium',
    'natural_feature',
    'beach',
    'mountain',
    'lake',
    'river',
    'waterfall',
    'historical_site',
    'monument',
    'cemetery',
  ];
  const hasStrongPlaceType = normalizedTypes.some(type =>
    strongPlaceTypes.some(pt => pt.toLowerCase() === type)
  );

  // If it's a strong place type, ALWAYS treat as place (even if it has business_status)
  // This includes places of worship that are also tourist attractions
  if (hasStrongPlaceType) {
    return false;
  }

  // PRIORITY 1: Check for business indicators (after excluding tourist attractions)
  // This ensures businesses with point_of_interest type are correctly identified

  // Check if any type matches business types (case-insensitive)
  // Note: "establishment" is a generic type that can appear in both businesses and places,
  // so we need to check it more carefully
  const businessTypesToCheck = BUSINESS_TYPES.filter(
    bt => bt !== 'establishment'
  );
  const hasBusinessType = normalizedTypes.some(type =>
    businessTypesToCheck.some(bt => bt.toLowerCase() === type)
  );

  // If it has business types (excluding generic "establishment"), it's a business
  if (hasBusinessType) {
    return true;
  }

  // Special handling for "establishment" type:
  // "establishment" alone is too generic - it appears in both businesses and places
  // Only treat as business if it has "establishment" AND other business-related types
  // AND no strong place types
  const hasEstablishment = normalizedTypes.includes('establishment');
  if (hasEstablishment && !hasStrongPlaceType) {
    // Check if it has other business-related types along with establishment
    const businessRelatedTypes = [
      'store',
      'shop',
      'restaurant',
      'cafe',
      'hotel',
      'lodging',
      'bank',
      'pharmacy',
      'hospital',
      'office',
    ];
    const hasBusinessRelatedType = normalizedTypes.some(type =>
      businessRelatedTypes.some(brt => type.includes(brt))
    );

    if (hasBusinessRelatedType) {
      return true; // Treat as business if it has establishment + other business types
    }
    // If only "establishment" without other indicators, we'll check point_of_interest later
    // and default to place if no business indicators found
  }

  // Check for common business keywords in type names
  // These indicate commercial establishments
  // Note: Exclude places of worship from business keywords check
  const hasPlaceOfWorshipType = normalizedTypes.some(type =>
    [
      'place_of_worship',
      'hindu_temple',
      'temple',
      'mosque',
      'church',
      'synagogue',
    ].some(pow => type.includes(pow))
  );

  const businessKeywords = [
    'store',
    'shop',
    'mall',
    'restaurant',
    'cafe',
    'coffee',
    'bar',
    'market',
    'hotel',
    'lodging',
    'bank',
    'pharmacy',
    'hospital',
    'clinic',
    'health', // Added for health centers, diagnostic centers
    'school',
    'dealer', // Added for car dealers, etc.
  ];
  const hasBusinessKeyword = normalizedTypes.some(type =>
    businessKeywords.some(keyword => type.includes(keyword))
  );

  // Only treat as business if it has business keywords AND it's not a place of worship
  if (hasBusinessKeyword && !hasPlaceOfWorshipType) {
    return true;
  }

  // Places of worship should be treated as places (unless they have specific commercial types)
  if (hasPlaceOfWorshipType) {
    // Only treat as business if it has commercial business types (restaurant, hotel, etc.)
    const commercialBusinessTypes = [
      'restaurant',
      'cafe',
      'bar',
      'hotel',
      'lodging',
      'store',
      'shop',
      'mall',
    ];
    const hasCommercialType = normalizedTypes.some(type =>
      commercialBusinessTypes.some(cbt => type.includes(cbt))
    );

    if (!hasCommercialType) {
      return false; // Treat as place
    }
  }

  // Note: business_status and opening_hours are no longer in the API response
  // All business/place determination is now based on types array only

  // PRIORITY 2: Check for point_of_interest type
  // point_of_interest should default to a place unless there are clear business indicators
  // This is important because many tourist attractions and places of interest
  // have point_of_interest as their primary type
  const hasPointOfInterest = normalizedTypes.includes('point_of_interest');

  if (hasPointOfInterest) {
    // Only treat as business if it has specific business types or keywords
    // AND it's not a place of worship
    if (!hasPlaceOfWorshipType && (hasBusinessType || hasBusinessKeyword)) {
      return true; // Has business indicators, treat as business
    }
    // Otherwise, point_of_interest defaults to place
    return false;
  }

  // Check for generic types (office, building, establishment)
  // These should default to place unless they have clear business indicators
  const genericTypes = ['office', 'building', 'establishment'];
  const hasOnlyGenericTypes = normalizedTypes.every(type =>
    genericTypes.some(gt => type.includes(gt))
  );

  if (hasOnlyGenericTypes) {
    // Generic types without other business indicators default to place
    return false;
  }

  // If location has "establishment" but no business indicators found,
  // and it's not a point_of_interest, default to place
  // This handles cases like ["establishment"] or ["establishment", "something_else"]
  // where "something_else" is not a business type
  if (hasEstablishment && !hasStrongPlaceType && !hasPointOfInterest) {
    if (!hasBusinessType && !hasBusinessKeyword) {
      return false; // Default to place
    }
  }

  // Default: not a business (will be treated as a place)
  // This includes any location that doesn't have clear business indicators
  // This ensures we err on the side of showing places rather than hiding them
  return false;
};

/**
 * Check if a location is a tourist place/attraction
 */
export const isPlaceLocation = (location: ChatLocation): boolean => {
  return !isBusinessLocation(location);
};

/**
 * Check if a location is a tourist attraction (for tourism-based apps)
 * Only returns true for actual tourist attractions, not businesses or administrative areas
 */
export const isTouristAttraction = (location: ChatLocation): boolean => {
  if (!location.types || location.types.length === 0) {
    return false;
  }

  // Normalize types to lowercase for comparison
  const normalizedTypes = location.types.map(t => t.toLowerCase().trim());

  // Tourist attraction types (actual places tourists visit)
  const touristAttractionTypes = [
    'tourist_attraction',
    'park',
    'museum',
    'art_gallery',
    'zoo',
    'aquarium',
    'amusement_park',
    'natural_feature',
    'beach',
    'mountain',
    'lake',
    'river',
    'waterfall',
    'historical_site',
    'monument',
    'campground',
    'rv_park',
  ];

  // Check if it has tourist attraction types
  const hasTouristAttractionType = normalizedTypes.some(type =>
    touristAttractionTypes.some(ta => ta.toLowerCase() === type)
  );

  if (hasTouristAttractionType) {
    return true;
  }

  // point_of_interest can be a tourist attraction, but only if it's not a business
  if (normalizedTypes.includes('point_of_interest')) {
    return !isBusinessLocation(location);
  }

  // Exclude pure business types
  if (isBusinessLocation(location)) {
    return false;
  }

  // Exclude administrative areas without attraction types
  const administrativeTypes = [
    'country',
    'administrative_area_level_1',
    'administrative_area_level_2',
    'administrative_area_level_3',
    'locality',
    'sublocality',
    'political',
  ];

  const isOnlyAdministrative = normalizedTypes.every(type =>
    administrativeTypes.some(at => at.toLowerCase() === type)
  );

  if (isOnlyAdministrative) {
    return false;
  }

  return false;
};
